import {InjectionToken} from '@angular/core'
import type {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'
import {CatalogOnlineEmbeddedService} from '@service/orders/catalog/catalog-online-embedded.service'
import {CatalogOnlineService} from '@service/orders/catalog/catalog-online.service'

/**
 * Factory que resuelve la implementación adecuada de `CatalogOnlineService`.
 *
 * @description
 * Devuelve `CatalogOnlineEmbeddedService` en modo embebido y
 * `CatalogOnlineService` en modo standalone. El servicio encapsula la
 * consulta del catálogo online (productos, precios y disponibilidad)
 * usada por el flujo de nuevo pedido cuando el dispositivo tiene
 * conexión.
 *
 * @param embeddedClientService Cliente que detecta si la app corre embebida.
 * @returns Instancia activa de `CatalogOnlineService`.
 */
export function providerCatalogOnlineServiceFactory(embeddedClientService: EmbeddedClientService) {
  return embeddedClientService.isEmbeddedFunction() ? new CatalogOnlineEmbeddedService() : new CatalogOnlineService()
}

/**
 * `InjectionToken` que referencia la instancia activa de `CatalogOnlineService`.
 *
 * @description
 * Registrado en `SERVICE_PROVIDERS` junto con
 * {@link providerCatalogOnlineServiceFactory}.
 */
export const CatalogOnlineServiceToken = new InjectionToken<CatalogOnlineService>('CatalogOnlineService')
/**
 * `InjectionToken` auxiliar asociado específicamente al servicio embebido.
 *
 * @description
 * Se expone para escenarios donde sea necesario inyectar la variante
 * embebida directamente (por ejemplo, testing o adaptación de host). En
 * uso habitual, inyecta `CatalogOnlineService` vía el alias declarado en
 * `providers.ts`.
 */
export const CatalogOnlineEmbeddedServiceToken = new InjectionToken<CatalogOnlineEmbeddedService>(
  'CatalogOnlineEmbeddedService'
)
